import { useQuery } from '@apollo/client'
import Money from '@/gf/modules/Money'
import { FETCH_GEARFLOW_SHIPPING_METRICS } from '@/gf/queries/metrics'
import { MetricsVars, Money as TMoney, MoneyPayload } from '../../types'

interface ShippingPerStoreGroupPayload {
  storeName: string
  netShippingAmount: MoneyPayload
  averageNetShippingAmount: MoneyPayload
  storeOrdersCount: number
}
interface ShippingPerStoreGroup extends Omit<
  ShippingPerStoreGroupPayload,
  'netShippingAmount' | 'averageNetShippingAmount'
> {
  netShippingAmount: TMoney
  averageNetShippingAmount: TMoney
}
interface StoreOrderMetricPayload {
  storeOrderId: string
  storeName: string
  labelCost: MoneyPayload
  storeOrderShippingCost: MoneyPayload
}
interface StoreOrderMetric extends Omit<
  StoreOrderMetricPayload,
  'labelCost' | 'storeOrderShippingCost'
> {
  labelCost: TMoney
  storeOrderShippingCost: TMoney
}
interface FetchGearflowShippingMetricsResultPayload {
  netShippingAmount: MoneyPayload
  averageNetShippingAmount: MoneyPayload
  storeOrders: StoreOrderMetricPayload[]
  storeOrdersCount: number
  shippingPerStoreGroups: ShippingPerStoreGroupPayload[]
}
interface FetchGearflowShippingMetricsResult extends Omit<
  FetchGearflowShippingMetricsResultPayload,
  'netShippingAmount' | 'averageNetShippingAmount' | 'storeOrders' | 'shippingPerStoreGroups'
> {
  netShippingAmount: TMoney
  averageNetShippingAmount: TMoney
  storeOrders: StoreOrderMetric[]
  shippingPerStoreGroups: ShippingPerStoreGroup[]
}
interface FetchGearflowShippingMetricsData {
  fetchGearflowShippingMetrics: FetchGearflowShippingMetricsResultPayload
}
type FetchGearflowShippingMetricsVars = MetricsVars

const useGearflowShippingMetrics = ({
  fromDate,
  toDate,
  selectedStoreId,
}: FetchGearflowShippingMetricsVars) => {
  const { loading, error, data } = useQuery<
    FetchGearflowShippingMetricsData,
    FetchGearflowShippingMetricsVars
  >(FETCH_GEARFLOW_SHIPPING_METRICS, {
    variables: { fromDate, toDate, selectedStoreId },
    fetchPolicy: 'no-cache',
  })

  let gearflowShippingMetricsResult: FetchGearflowShippingMetricsResult | undefined
  if (data) {
    gearflowShippingMetricsResult = {
      ...data.fetchGearflowShippingMetrics,
      netShippingAmount: Money.fromPayload(data.fetchGearflowShippingMetrics.netShippingAmount),
      averageNetShippingAmount: Money.fromPayload(
        data.fetchGearflowShippingMetrics.averageNetShippingAmount
      ),
      storeOrders: data.fetchGearflowShippingMetrics.storeOrders.map(
        (storeOrderMetric): StoreOrderMetric => ({
          ...storeOrderMetric,
          labelCost: Money.fromPayload(storeOrderMetric.labelCost),
          storeOrderShippingCost: Money.fromPayload(storeOrderMetric.storeOrderShippingCost),
        })
      ),
      shippingPerStoreGroups: data.fetchGearflowShippingMetrics.shippingPerStoreGroups.map(
        (shippingPerStoreGroup) => ({
          ...shippingPerStoreGroup,
          averageNetShippingAmount: Money.fromPayload(
            shippingPerStoreGroup.averageNetShippingAmount
          ),
          netShippingAmount: Money.fromPayload(shippingPerStoreGroup.netShippingAmount),
        })
      ),
    }
  }
  return { loading, error, data: gearflowShippingMetricsResult }
}

export default useGearflowShippingMetrics
